import { useState } from 'react'
import {
  useAccessKeyLoans,
  useAccessKeySummary,
  useAccessKeyTypes,
  useAccessKeys,
  useCustomers,
  useDeleteAccessKey,
  useHandOutAccessKey,
  useReturnAccessKey,
  useSaveAccessKey,
  useUsers
} from '../api/queries'
import type { AccessKey, AccessKeyInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'

function formatDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Hvor længe nøglen har været ude, som en hel sætning.
 *
 * Hele sætningen frem for bare tallet: "Ude i" + "i dag" bliver til "Ude i i
 * dag", og et tal alene siger ikke, om nøglen er glemt eller lige hentet.
 */
function describeTimeOut(days: number) {
  if (days === 0) return 'Udleveret i dag'
  if (days === 1) return 'Ude i 1 dag'
  if (days < 60) return `Ude i ${days} dage`
  return `Ude i ${Math.round(days / 30)} måneder`
}

/** Hvor længe den var ude, til historikken: "samme dag", "3 dage", "2 måneder". */
function describeSpan(days: number) {
  if (days === 0) return 'samme dag'
  if (days === 1) return '1 dag'
  if (days < 60) return `${days} dage`
  return `${Math.round(days / 30)} måneder`
}

/**
 * Nøgler, brikker og adgangskort (beslutning 055).
 *
 * Siden svarer på ét spørgsmål: hvem har nøglen til det her sted, og er den
 * kommet tilbage. Dem der er ude står øverst.
 *
 * Selve nøglen er stamdata og kræver Administrator; udlevering kræver
 * Projektleder, og den der har en nøgle må selv aflevere den.
 */
export function AccessKeysPage() {
  const { user, isAdmin, isProjectManager } = useAuth()
  const [search, setSearch] = useState('')
  const [outOnly, setOutOnly] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<AccessKey | null | undefined>(undefined)
  const [handingOut, setHandingOut] = useState<AccessKey | null>(null)
  const [history, setHistory] = useState<AccessKey | null>(null)

  const { data, isLoading, error } = useAccessKeys({ search, outOnly, includeInactive })
  const { data: summary } = useAccessKeySummary()
  const remove = useDeleteAccessKey()
  const returnKey = useReturnAccessKey()

  const [actionError, setActionError] = useState<string | null>(null)

  async function handleReturn(key: AccessKey) {
    setActionError(null)
    const holder = key.currentLoan?.user.displayName ?? 'nogen'
    if (!confirm(`Registrér "${key.name}" som afleveret af ${holder}?`)) return

    try {
      await returnKey.mutateAsync({ id: key.id, note: null })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Nøglen kunne ikke registreres som afleveret.')
    }
  }

  async function handleDelete(key: AccessKey) {
    setActionError(null)
    if (!confirm(`Slet "${key.name}" og hele dens historik?\n\nEr nøglen bortkommet eller inddraget, så fjern i stedet fluebenet "Nøglen findes stadig" - så bliver historikken stående.`)) {
      return
    }

    try {
      await remove.mutateAsync(key.id)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Nøglen kunne ikke slettes.')
    }
  }

  /** Må jeg aflevere den her nøgle? Projektledere må alle, andre kun deres egen. */
  const canReturn = (key: AccessKey) =>
    key.currentLoan !== null &&
    key.currentLoan !== undefined &&
    (isProjectManager || key.currentLoan.user.id === user?.id)

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {summary ? `${summary.out} af ${summary.total} ude` : `${data?.length ?? 0} nøgler`}
        </div>

        <input
          placeholder="Søg i navn, mærkning, sted eller kunde ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={outOnly} onChange={(e) => setOutOnly(e.target.checked)} />
          Kun dem der er ude
        </label>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Vis bortkomne
        </label>

        {isAdmin && (
          <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>
            Opret nøgle
          </button>
        )}
      </div>

      {summary && summary.longOut > 0 && (
        <div className="banner" style={{ marginBottom: 12, borderLeft: '4px solid #D97706' }}>
          <strong>
            {summary.longOut} {summary.longOut === 1 ? 'nøgle har' : 'nøgler har'} været ude i mere
            end {summary.longOutDays} dage.
          </strong>{' '}
          Det behøver ikke være galt - en lang sag kan sagtens kræve det - men det er værd at
          kigge på, når der ryddes op.
        </div>
      )}

      {actionError && <ErrorBox error={actionError} />}
      {error && <ErrorBox error={error} />}
      {isLoading && <Loading />}

      {data && data.length === 0 && (
        <EmptyState>
          {search || outOnly
            ? 'Ingen nøgler passer på det, du søger efter.'
            : isAdmin
              ? 'Der står ingen nøgler endnu. Tryk Opret nøgle - én række pr. fysisk nøgle, så man kan se, hvem der har hvilken.'
              : 'Der står ingen nøgler endnu. En administrator kan skrive dem ind.'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 8 }}>
            {data.map((k) => (
              <div
                key={k.id}
                className="row"
                style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', opacity: k.isActive ? 1 : 0.55 }}
              >
                <span
                  style={{
                    width: 6,
                    alignSelf: 'stretch',
                    minHeight: 40,
                    borderRadius: 3,
                    background: k.currentLoan ? '#D97706' : 'transparent',
                    flexShrink: 0
                  }}
                />

                <div style={{ minWidth: 190, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {k.name}
                    {k.identifier && <span className="tiny muted"> · {k.identifier}</span>}
                    {!k.isActive && <span className="tiny muted"> · bortkommet</span>}
                  </div>
                  <div className="small muted">
                    {k.keyTypeName} · {k.customerName ?? 'Husets egen'}
                    {k.location && ` · ${k.location}`}
                  </div>
                  {k.notes && <div className="small">{k.notes}</div>}
                </div>

                <div style={{ minWidth: 200 }}>
                  {k.currentLoan ? (
                    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <Avatar user={k.currentLoan.user} size={26} />
                      <div>
                        <div className="small" style={{ fontWeight: 600 }}>
                          {k.currentLoan.user.displayName}
                        </div>
                        <div className="tiny muted">
                          {describeTimeOut(k.currentLoan.daysOut)} · siden{' '}
                          {formatDate(k.currentLoan.handedOutOn)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="small muted">
                      {k.storedAt ? `Ligger i ${k.storedAt}` : 'Ikke udleveret'}
                    </div>
                  )}
                </div>

                <div className="row" style={{ gap: 6 }}>
                  <button className="btn btn--sm" onClick={() => setHistory(k)}>
                    Historik
                  </button>

                  {k.currentLoan
                    ? canReturn(k) && (
                        <button className="btn btn--sm" onClick={() => void handleReturn(k)}>
                          Afleveret
                        </button>
                      )
                    : isProjectManager &&
                      k.isActive && (
                        <button className="btn btn--sm" onClick={() => setHandingOut(k)}>
                          Udlevér
                        </button>
                      )}

                  {isAdmin && (
                    <>
                      <button className="btn btn--sm" onClick={() => setEditing(k)}>
                        Ret
                      </button>
                      <button className="btn btn--danger btn--sm" onClick={() => void handleDelete(k)}>
                        Slet
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {editing !== undefined && (
        <AccessKeyDialog accessKey={editing} onClose={() => setEditing(undefined)} />
      )}

      {handingOut && (
        <HandOutDialog accessKey={handingOut} onClose={() => setHandingOut(null)} />
      )}

      {history && <HistoryDialog accessKey={history} onClose={() => setHistory(null)} />}
    </>
  )
}

/** Udlevering: hvem får nøglen, og hvorfor. */
function HandOutDialog({ accessKey, onClose }: { accessKey: AccessKey; onClose: () => void }) {
  const { data: users } = useUsers()
  const handOut = useHandOutAccessKey()
  const [userId, setUserId] = useState(0)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await handOut.mutateAsync({ id: accessKey.id, userId, note: note.trim() || null })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nøglen kunne ikke udleveres.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Udlevér {accessKey.name}</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          {accessKey.keyTypeName}
          {accessKey.identifier && ` · ${accessKey.identifier}`} ·{' '}
          {accessKey.customerName ?? 'Husets egen'}
        </p>

        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span className="field__label">Hvem får den</span>
            <select value={userId} required onChange={(e) => setUserId(Number(e.target.value))}>
              <option value={0} disabled>
                Vælg medarbejder ...
              </option>
              {(users ?? []).filter((u) => u.isActive).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Hvorfor (valgfri)</span>
            <input
              value={note}
              placeholder="F.eks. serviceeftersyn uge 38"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          {error && <ErrorBox error={error} />}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn--primary" type="submit" disabled={handOut.isPending || !userId}>
              {handOut.isPending ? 'Gemmer ...' : 'Udlevér'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Annullér
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** Hele historikken - hvem har haft nøglen hvornår. */
function HistoryDialog({ accessKey, onClose }: { accessKey: AccessKey; onClose: () => void }) {
  const { data, isLoading } = useAccessKeyLoans(accessKey.id)

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>{accessKey.name}</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Hvem har haft den, og hvornår. Historikken bliver stående - ved en bortkommet nøgle er
          det netop spørgsmålet, hvem der havde den dengang.
        </p>

        {isLoading && <Loading />}

        {data && data.length === 0 && (
          <EmptyState>Nøglen har aldrig været udleveret.</EmptyState>
        )}

        <div className="stack" style={{ gap: 8 }}>
          {(data ?? []).map((l) => (
            <div key={l.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
              <Avatar user={l.user} size={26} />
              <div style={{ flex: 1 }}>
                <div className="small" style={{ fontWeight: 600 }}>
                  {l.user.displayName}
                  {!l.returnedOn && <span style={{ color: '#D97706' }}> · har den nu</span>}
                </div>
                <div className="tiny muted">
                  {formatDate(l.handedOutOn)} -{' '}
                  {l.returnedOn ? formatDate(l.returnedOn) : 'ikke afleveret'} ·{' '}
                  {describeSpan(l.daysOut)}
                </div>
                {l.note && <div className="tiny">{l.note}</div>}
              </div>
            </div>
          ))}
        </div>

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn" onClick={onClose}>
            Luk
          </button>
        </div>
      </div>
    </div>
  )
}

function AccessKeyDialog({
  accessKey,
  onClose
}: {
  accessKey: AccessKey | null
  onClose: () => void
}) {
  const { data: customers } = useCustomers()
  const { data: types } = useAccessKeyTypes()
  const save = useSaveAccessKey()

  const [form, setForm] = useState<AccessKeyInput>({
    name: accessKey?.name ?? '',
    keyType: accessKey?.keyType ?? 'key',
    customerId: accessKey?.customerId ?? null,
    identifier: accessKey?.identifier ?? '',
    location: accessKey?.location ?? '',
    storedAt: accessKey?.storedAt ?? '',
    notes: accessKey?.notes ?? '',
    isActive: accessKey?.isActive ?? true
  })
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: accessKey?.id,
        body: {
          ...form,
          identifier: form.identifier?.trim() || null,
          location: form.location?.trim() || null,
          storedAt: form.storedAt?.trim() || null,
          notes: form.notes?.trim() || null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nøglen kunne ikke gemmes.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
          {accessKey ? 'Ret nøgle' : 'Opret nøgle'}
        </h2>

        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span className="field__label">Hvad giver den adgang til</span>
            <input
              value={form.name}
              required
              placeholder="F.eks. Teknikrum, kælder"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>

          <div className="row" style={{ gap: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Type</span>
              <select
                value={form.keyType}
                onChange={(e) => setForm({ ...form, keyType: e.target.value })}
              >
                {(types ?? []).map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Mærkning</span>
              <input
                value={form.identifier ?? ''}
                placeholder="F.eks. A-14"
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">Kunde</span>
            <select
              value={form.customerId ?? 0}
              onChange={(e) =>
                setForm({ ...form, customerId: Number(e.target.value) || null })
              }
            >
              <option value={0}>Husets egen</option>
              {(customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <div className="row" style={{ gap: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Hvor den passer</span>
              <input
                value={form.location ?? ''}
                placeholder="F.eks. Hovedindgang"
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Hvor den ligger</span>
              <input
                value={form.storedAt ?? ''}
                placeholder="F.eks. Nøgleskab, kontoret"
                onChange={(e) => setForm({ ...form, storedAt: e.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">Note</span>
            <input
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>

          {/* Selve koden hoerer ikke hjemme her - alle indloggede kan laese siden,
              og en udleveret kode kan ikke tages tilbage (beslutning 055). */}
          {form.keyType === 'code' && (
            <p className="small" style={{ marginTop: 0, color: '#D97706' }}>
              <strong>Skriv ikke selve koden.</strong> Alle indloggede kan læse siden, og en kode,
              der først er sluppet ud, kan ikke tages tilbage. Rækken siger, at der findes en kode,
              og hvem der har fået den at vide.
            </p>
          )}

          <label className="cal-person" style={{ cursor: 'pointer', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Nøglen findes stadig
          </label>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            Fjern fluebenet, når nøglen er bortkommet eller inddraget. Så bliver historikken
            stående, men nøglen kan ikke udleveres igen.
          </p>

          {error && <ErrorBox error={error} />}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn--primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Gemmer ...' : 'Gem'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Annullér
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
